import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Box, Wrench, CreditCard, Activity, PackageCheck, Hammer, PhoneCall } from "lucide-react";
import SiteVisitForm from "@/components/SiteVisitForm";
import { PHONE_HREF, PHONE_DISPLAY, trackPhoneCall } from "@/lib/contact";

const SERVICES_IMG =
  "/media/vending-machine-orange-wall.png";

export default function Services() {
  return (
    <div data-testid="services-page" className="text-white">
      <Seo />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-radial-fade" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center rounded-full border border-[#FE7200]/30 bg-[#FE7200]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#FE7200]">
              Anytime Vending Machines
            </div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              <span className="text-[#FE7200]">Vending Services</span> for Qualified Businesses
            </h1>
            <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl">
              We supply, install, stock and service modern vending machines. Qualifying sites can receive a machine for <b className="text-white">$0</b>.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="#book-form"
                data-testid="services-book-button"
                className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
              >
                Book Free Site Visit
              </a>
              <Link
                to="/"
                data-testid="services-home-button"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10 transition-colors"
              >
                Back to Home
              </Link>
              <a
                href={PHONE_HREF}
                onClick={trackPhoneCall}
                data-testid="services-call-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#FE7200]/50 bg-[#FE7200]/15 px-6 py-3 text-sm font-bold uppercase tracking-wider text-[#FF8C2E] hover:bg-[#FE7200]/25 transition-colors"
              >
                <PhoneCall className="h-4 w-4" /> Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#121212]">
              <img src={SERVICES_IMG} alt="Vending machine" className="w-full h-96 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28" data-testid="our-services-section">
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Our Services</h2>
        <p className="mt-3 text-zinc-300">We manage vending end-to-end so you don't lift a finger.</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Box, title: "$0 Machine Supply", items: ["No rental fees", "No servicing costs", "30+ staff or foot traffic"] },
            { icon: Hammer, title: "Installation", items: ["Fast setup after site visit", "Professional placement", "Standard power only"] },
            { icon: PackageCheck, title: "Stocking", items: ["Drinks, snacks & meals", "Healthy options available", "Customised mix"] },
            { icon: Wrench, title: "Servicing", items: ["Repairs & maintenance", "Clean & reliable", "Fully managed"] },
            { icon: CreditCard, title: "Payments", items: ["Tap & Go", "Cash & coin", "Fast checkout"] },
            { icon: Activity, title: "Monitoring", items: ["Automatic stock alerts", "Reduced downtime", "Optimised refills"] },
          ].map((card, idx) => (
            <div
              key={card.title}
              data-testid={`service-card-${idx}`}
              className="group rounded-2xl border border-white/10 bg-[#121212] p-6 hover:border-[#FE7200]/40 hover:-translate-y-1 transition-all"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FE7200]/15 border border-[#FE7200]/30 text-[#FE7200]">
                <card.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-white">{card.title}</h3>
              <ul className="mt-3 space-y-1.5 text-sm text-zinc-300">
                {card.items.map((it) => (
                  <li key={it} className="flex gap-2 items-start">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FE7200]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-8 text-sm md:text-base text-zinc-300 leading-relaxed">
          <p>
            At <b className="text-white">Anytime Vending Machines</b>, our service is designed to remove every common
            frustration businesses have with traditional vending. Old machines, unreliable servicing, cash-only
            payments and constant issues are the norm across many sites — and that's exactly what we set out to
            change. For qualifying workplaces, we provide modern vending machines at <b className="text-white">no upfront cost</b>.
            That means no purchase price, no rental fees, and no ongoing servicing costs. If your site has
            <b className="text-white"> 30 or more staff </b>or consistent daily foot traffic, you may be eligible to
            receive a professionally installed machine for $0. We earn from product sales, not from charging your
            business.
          </p>
          <p className="mt-4">
            Our team handles the entire process end-to-end. After a quick site visit, we recommend the most suitable
            machine, placement and product mix based on your staff and environment. Once approved, we deliver and
            install the machine, ensure it's safety tested, and have it ready to use immediately. Stocking and
            servicing are fully managed by us. Machines are monitored remotely, allowing us to restock before
            products run out and address issues quickly.
          </p>
          <p className="mt-4">
            Payment flexibility is standard across our machines. Staff can pay using Tap &amp; Go, cards, phones,
            smart watches, or cash and coin. We also offer risk-free trial options. If a machine isn't being used
            or doesn't suit your site, we'll remove it — no contracts, no penalties, no hassle. Our goal is simple:
            provide vending that staff actually use and businesses don't have to think about.
          </p>
        </div>

        <div className="mt-10 rounded-2xl border border-[#FE7200]/30 bg-gradient-to-r from-[#FE7200]/15 to-transparent p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="font-display text-xl md:text-2xl font-bold text-white">
              Think you qualify for a free machine?
            </div>
            <p className="text-sm text-zinc-300 mt-1">Book a site visit and we'll confirm eligibility.</p>
          </div>
          <div className="flex gap-3">
            <a
              href="#book-form"
              data-testid="services-cta-book"
              className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
            >
              Book Free Site Visit
            </a>
            <a
              href={PHONE_HREF}
              onClick={trackPhoneCall}
              data-testid="services-cta-call"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#FE7200]/50 bg-[#FE7200]/15 px-6 py-3 text-sm font-bold uppercase tracking-wider text-[#FF8C2E] hover:bg-[#FE7200]/25 transition-colors"
            >
              <PhoneCall className="h-4 w-4" /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      <section id="book-form" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-16 border-t border-white/5" data-testid="services-form-section">
        <SiteVisitForm source="services-page" testIdPrefix="services-booking" />
      </section>
    </div>
  );
}
